import React, { FC, useRef } from "react"; 
import LibraryMediaCardIcon from "../../../../../images/media-cards/library.svg";
import UploadMediaCardIcon from "../../../../../images/media-cards/upload.svg";
import DeleteIcon from "../../../../../images/products/trash-white.svg";
import StarIcon from "../../../../../images/products/star-white.svg";
import StarYellowIcon from "../../../../../images/products/star-yellow.svg";
import LibraryIcon from "../../../../../images/common/library-grey-folder.svg";
import UploadIcon from "../../../../../images/common/upload-grey-shape.svg"; 
import MediaLibraryPopUp from "./MediaLibraryPopUp";  
import { MediaTypes } from "../enums";
import { MediaModel } from "../models";
import { CustomLabel, FlexContainer, LibraryButtons, ToastService, Video } from "@mindmemobile/mm-shared-global";
import { AuthService, MediaService } from "../services"; 
import { DescriptionCell, ItemFrame, ItemImage, LabelCell, MediaBoxesWrapper } from "../styles";
import { MediaStore } from "../store";


type IProps = {
    hideLabel?: boolean;
    allowedMediaTypes?: MediaTypes[];
    medias?: MediaModel[];
    //onLibrary: () => void;
    onUpload: (medias: MediaModel[]) => void;
    onDelete: (media: MediaModel) => void;
    multiple: boolean;
    onSelectLibraryMedia: (media: MediaModel) => void;
    onFavourite?: (media: MediaModel) => void;
    showUploadButtons?: boolean;
    hideOverlays?: boolean;
    allowOnlyOne?: boolean;
};
const MediaBoxes: FC<IProps> = (props: IProps) => {
    const fileUploadImageRef = useRef<HTMLInputElement>(null);
    const fileUploadBtnRef = useRef<HTMLInputElement>(null);
    const [showLibrary, toggleLibrary] = React.useState(false);
    const { showUploadButtons = true, hideOverlays = false, allowOnlyOne = false } = props;

    function onUploadMedia(files: FileList | null) {
        if (allowOnlyOne && files && files.length > 1) {
            ToastService.showWarning('You are only allowed to upload a maximum of 1 file');
            return false;
        }
        if (files && files.length > 3) {
            ToastService.showWarning('You are only allowed to upload a maximum of 3 files');
            return false;
        }
        const isImageOnly =
            props.allowedMediaTypes && props.allowedMediaTypes?.length > 0
                ? props.allowedMediaTypes?.includes(MediaTypes.Image) && props.allowedMediaTypes.length === 1
                : false;

        const filteredList = Array.from(files || []).filter((file) => {
            const isImage = file.type.startsWith('image/');
            const isAllowedType = isImageOnly ? isImage : true;
            return file.size / 1048576 <= 5 && isAllowedType;
        });

        if (isImageOnly && Array.from(files || []).some((file) => !file.type.startsWith('image/'))) {
            ToastService.showError('Only images are allowed');
            return;
        }

        if (Array.from(files || []).length > 0 && filteredList.length === 0) {
            if (isImageOnly) {
                ToastService.showError('Please upload image files less than 5MB');
                return;
            } else {
                ToastService.showError('Please upload files less than 5MB');
                return;
            }
        }

        MediaService.uploadMedia({
            AccountId: AuthService.authData.accountId,
            Files: filteredList,
        }).subscribe({
            next: (result) => {
                if (result?.error) {
                    ToastService.showError(result.error);
                    return;
                }
                const medias = result.medias?.map((m) => MediaModel.deserializeAPIModel(m)) || [];
                const nonDuplicateMedias = medias.filter((media) => !isMediaDuplicate(media));

                if (nonDuplicateMedias.length === 0) {
                    ToastService.showError('Media is already present.');
                } else {
                    props.onUpload(nonDuplicateMedias);
                    ToastService.showSuccess('Media uploaded successfully');
                }
            },
            error: () => {
                ToastService.showError('Error in uploading media. Please try again later.');
            },
        });
    }

    const uploadClick = () => {
        if (fileUploadImageRef.current) {
            fileUploadImageRef.current.click();
        } else if (fileUploadBtnRef.current) {
            fileUploadBtnRef.current.click();
        }
    };

    const renderMedia = (media: MediaModel) => {
        switch (media.type) {
            case MediaTypes.Video:
                return <Video url={media.fullURL} />;
            default:
                return <img src={media.fullURL} className="product-image" />;
        }
    };

    const isMediaDuplicate = (media: MediaModel): boolean => {
        return props.medias?.some((existingMedia) => existingMedia.mediaId === media.mediaId) || false;
    };

    return (
        <MediaBoxesWrapper>
            {showLibrary && (
                <MediaLibraryPopUp
                    allowedMediaTypes={props.allowedMediaTypes}
                    mediaStore={new MediaStore()}
                    onUpload={(media: MediaModel) => {
                        toggleLibrary(false);
                        if (!isMediaDuplicate(media)) {
                            props.onSelectLibraryMedia && props.onSelectLibraryMedia(media);
                        } else {
                            ToastService.showError('Media is already present.');
                        }
                    }}
                    onClose={() => toggleLibrary(false)}
                />
            )}
            <FlexContainer className="label-description-row">
                {!props.hideLabel && (
                    <LabelCell>
                        <CustomLabel>Media</CustomLabel>
                    </LabelCell>
                )}
                <DescriptionCell>
                    {props.medias && props.medias.length > 0 ? (
                        <>
                            {showUploadButtons && (
                                <div>
                                    <FlexContainer padding="5px 0px 25px" spacing={20}>
                                        <LibraryButtons
                                            className="afterpay-library-btn"
                                            onClick={() => toggleLibrary(true)}
                                        >
                                            <img src={LibraryIcon} alt="Library Icon" />
                                            Library
                                        </LibraryButtons>
                                        <input
                                            style={{ display: 'none' }}
                                            type="file"
                                            id="fileUploadButton"
                                            multiple={props.multiple}
                                            accept="video/*,image/*"
                                            ref={fileUploadBtnRef}
                                            onChange={(evt) => onUploadMedia(evt.currentTarget.files)}
                                        />
                                        <LibraryButtons
                                            className="afterpay-library-btn ap-upload-btn"
                                            onClick={uploadClick}
                                        >
                                            <img src={UploadIcon} alt="Upload Icon" />
                                            Upload
                                        </LibraryButtons>
                                    </FlexContainer>
                                </div>
                            )}
                            <div>
                                <FlexContainer spacing={15} fxWrap="wrap" className="item-frame-wrapper">
                                    {props.medias.map((media, idx) => (
                                        <ItemFrame key={idx}>
                                            <div>
                                                <ItemImage className="uploaded-image" 
                                                bgColor="#fff" // asked by Jeff to remove the bgcolor for item detail page 
                                                >
                                                    {renderMedia(media)}
                                                    {!hideOverlays && (
                                                        <div className="overlay-product">
                                                            <FlexContainer
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                className="full-height"
                                                                spacing={10}
                                                            >
                                                                <img
                                                                    onClick={() => props.onDelete(media)}
                                                                    src={DeleteIcon}
                                                                    alt="Delete Icon"
                                                                    // className="plus-hovered"
                                                                />
                                                                {/* <img
                                                                onClick={() => {}}
                                                                src={EditIcon}
                                                                alt="Edit Icon"
                                                                // className="plus-hovered"
                                                            /> */}
                                                                {media.type !== MediaTypes.Video &&
                                                                    !media.isFavourite &&
                                                                    props.onFavourite && (
                                                                        <img
                                                                            onClick={() => props.onFavourite?.(media)}
                                                                            src={StarIcon}
                                                                            alt="Favourite Icon"
                                                                        />
                                                                    )}
                                                            </FlexContainer>
                                                        </div>
                                                    )}
                                                    {!hideOverlays && media.isFavourite && (
                                                        <img
                                                            src={StarYellowIcon}
                                                            alt="Star Icon"
                                                            className="favourite-star"
                                                        />
                                                    )}
                                                </ItemImage>
                                            </div>
                                        </ItemFrame>
                                    ))}
                                </FlexContainer>
                            </div>
                        </>
                    ) : (
                        <FlexContainer spacing={15} className="media-cards">
                            <div>
                                <img
                                    onClick={() => toggleLibrary(true)}
                                    src={LibraryMediaCardIcon}
                                    alt="Library Media Card Icon"
                                />
                            </div>
                            <div>
                                <input
                                    style={{ display: 'none' }}
                                    type="file"
                                    id="fileInput"
                                    multiple={true}
                                    ref={fileUploadImageRef}
                                    accept="video/*,image/*"
                                    onChange={(evt) => onUploadMedia(evt.currentTarget.files)}
                                />
                                <img
                                    onClick={() => uploadClick()}
                                    src={UploadMediaCardIcon}
                                    alt="Upload Media Card Icon"
                                />
                            </div>
                        </FlexContainer>
                    )}
                </DescriptionCell>
            </FlexContainer>
        </MediaBoxesWrapper>
    );
};
export default MediaBoxes;
